import React, { useRef, useEffect, useMemo } from 'react';
import JoditEditor from 'jodit-react';
import { ReportData } from '../types/report.types';
import { getDefaultFieldContent, HtmlSectionField } from '../utils/defaultContent';
import 'jodit/es2021/jodit.min.css';

interface HtmlEditorProps {
  field: keyof ReportData;
  title: string;
  reportData: ReportData;
  onHtmlChange: (field: keyof ReportData, value: string) => void;
  useJodit?: boolean;
}

const HtmlEditor: React.FC<HtmlEditorProps> = ({ field, title, reportData, onHtmlChange, useJodit = false }) => {
  const htmlContent = (reportData[field] as string) || getDefaultFieldContent(field as HtmlSectionField, reportData);
  const divRef = useRef<HTMLDivElement>(null);
  const lastFieldRef = useRef<keyof ReportData | null>(null);
  const editorRef = useRef<any>(null);
  const changeTimerRef = useRef<number | null>(null);
  const lastUserEditTsRef = useRef<number>(0);

  // Uncontrolled sync effect for non-Jodit contentEditable div on field change
  useEffect(() => {
    if (!useJodit) {
      if (divRef.current && lastFieldRef.current !== field) {
        divRef.current.innerHTML = htmlContent;
        lastFieldRef.current = field;
      }
    }
  }, [field, htmlContent, useJodit]);

  // Sync external value to contentEditable div only when not focused
  useEffect(() => {
    if (!useJodit) {
      if (divRef.current && document.activeElement !== divRef.current && divRef.current.innerHTML !== htmlContent) {
        divRef.current.innerHTML = htmlContent;
      }
    }
  }, [htmlContent, useJodit]);

  useEffect(() => {
    return () => {
      if (changeTimerRef.current) {
        clearTimeout(changeTimerRef.current);
      }
    };
  }, []);

  const config = useMemo(() => ({
    readonly: false,
    toolbar: true,
    spellcheck: true,
    language: "en",
    toolbarButtonSize: "small" as const,
    toolbarSticky: true,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    enterMode: "p",
    buttons: [
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "outdent",
      "indent",
      "|",
      "font",
      "fontsize",
      "brush",
      "formatblock",
      "|",
      "table",
      "link",
      "|",
      "align",
      "undo",
      "redo",
      "|",
      "source",
      "fullsize"
    ],
    height: 300,
    minHeight: 200,
    maxHeight: 600,
  }), []);

  // Compute Jodit value only when field changes to prevent cursor reset on keystrokes
  const initialJoditContent = useMemo(() => htmlContent, [field]);

  if (useJodit) {
    return (
      <div className="space-y-4">
        {title && (
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          </div>
        )}
        <JoditEditor
          ref={editorRef}
          value={initialJoditContent}
          config={config}
          onBlur={(newContent) => {
            if (changeTimerRef.current) {
              clearTimeout(changeTimerRef.current);
              changeTimerRef.current = null;
            }
            onHtmlChange(field, newContent);
          }}
          onChange={(newContent) => {
            if (newContent !== htmlContent) {
              lastUserEditTsRef.current = Date.now();
              if (changeTimerRef.current) clearTimeout(changeTimerRef.current);
              changeTimerRef.current = window.setTimeout(() => {
                onHtmlChange(field, newContent);
              }, 150);
            }
          }}
        />
        {/* Sync external value to editor if idle and out of sync */}
        {(() => {
          const idleMs = Date.now() - lastUserEditTsRef.current;
          if (idleMs > 500 && editorRef.current && htmlContent !== editorRef.current.value) {
            try {
              editorRef.current.value = htmlContent;
            } catch {}
          }
          return null;
        })()}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
      )}
      <div
        ref={divRef}
        className="prose max-w-none min-h-[200px] p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        contentEditable
        onBlur={(e) => onHtmlChange(field, e.currentTarget.innerHTML)}
        onInput={(e) => {
          lastUserEditTsRef.current = Date.now();
          onHtmlChange(field, e.currentTarget.innerHTML);
        }}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default HtmlEditor;